// Wave R — Voice-to-Design hook & helpers.
import { useCallback, useEffect, useRef, useState } from "react";
import api from "./api";

// ────────────────────────────────────────────────────────────────────
// Global voice settings cache (Wave R.2) — read once per session, used by
// MicButton instances so each consumer doesn't have to thread language
// through props. Updated whenever VoiceSettings modal saves.
// ────────────────────────────────────────────────────────────────────
const VOICE_PREFS_KEY = "rachana_voice_settings";
let _prefsCache = null;
const _prefsListeners = new Set();

const _readCache = () => {
  if (_prefsCache) return _prefsCache;
  try {
    const raw = localStorage.getItem(VOICE_PREFS_KEY);
    if (raw) _prefsCache = JSON.parse(raw);
  } catch (_) { /* ok */ }
  if (!_prefsCache) {
    // Spec §3: TTS default = OFF on desktop, ON on mobile/tablet (≤1024 px).
    const isMobileOrTablet = typeof window !== "undefined"
        && window.innerWidth <= 1024;
    _prefsCache = { mode: "push_to_talk", language: "en",
                    feedback: isMobileOrTablet, wake_word: "Hey Capexara Process Industry",
                    store_transcripts: false };
  }
  return _prefsCache;
};

export function setVoicePrefs(p) {
  _prefsCache = { ..._readCache(), ...(p || {}) };
  try { localStorage.setItem(VOICE_PREFS_KEY, JSON.stringify(_prefsCache)); }
  catch (_) { /* ok */ }
  _prefsListeners.forEach(fn => { try { fn(_prefsCache); } catch (_) {} });
}

export function getVoicePrefs() { return _readCache(); }

export function useVoicePrefs() {
  const [p, setP] = useState(_readCache());
  useEffect(() => {
    const fn = (np) => setP({ ...np });
    _prefsListeners.add(fn);
    return () => { _prefsListeners.delete(fn); };
  }, []);
  return p;
}

// Refresh prefs from server on demand (called once at app startup).
export async function refreshVoicePrefsFromServer() {
  try {
    const r = await api.get("/voice/settings");
    if (r.data) setVoicePrefs(r.data);
  } catch (_) { /* not logged in or no prefs yet */ }
}

/**
 * useVoiceToDesign — manages MediaRecorder, sends to /api/voice/transcribe,
 * applies engineering vocabulary corrections and returns the corrected
 * transcript. Also exposes Web Speech API fallback for offline / fast mode.
 *
 * Returns:
 *   { isListening, isProcessing, start, stop, toggle, transcript, raw,
 *      hadCorrections, intent, error }
 */
export default function useVoiceToDesign({
  language: languageProp = null,
  prompt = null,
  onTranscript,
  onAutoSubmit,
  preferWhisper = true,
} = {}) {
  // Read default language from saved voice prefs unless overridden.
  const prefs = useVoicePrefs();
  const language = languageProp || prefs.language || "en";
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [raw, setRaw] = useState("");
  const [hadCorrections, setHadCorrections] = useState(false);
  const [intent, setIntent] = useState("");
  const [autoSubmit, setAutoSubmit] = useState(false);
  const [error, setError] = useState("");

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const fallbackRecRef = useRef(null);

  const reset = () => {
    setError("");
    setTranscript("");
    setRaw("");
    setHadCorrections(false);
    setIntent("");
  };

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  const useWhisper = preferWhisper && typeof MediaRecorder !== "undefined";

  const start = useCallback(async () => {
    reset();
    if (useWhisper) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;
        const rec = new MediaRecorder(stream);
        chunksRef.current = [];
        rec.ondataavailable = (e) => {
          if (e.data.size > 0) chunksRef.current.push(e.data);
        };
        rec.onstop = async () => {
          stopStream();
          const blob = new Blob(chunksRef.current, { type: "audio/webm" });
          if (blob.size < 1000) {
            setError("Audio too short — keep talking longer");
            setIsProcessing(false);
            return;
          }
          setIsProcessing(true);
          try {
            const fd = new FormData();
            fd.append("audio", blob, "voice.webm");
            fd.append("language", language);
            if (prompt) fd.append("prompt", prompt);
            const r = await api.post("/voice/transcribe", fd,
                  { headers: { "Content-Type": "multipart/form-data" } });
            const d = r.data;
            setTranscript(d.transcript);
            setRaw(d.raw);
            setHadCorrections(d.had_corrections);
            setIntent(d.intent);
            setAutoSubmit(!!d.auto_submit);
            onTranscript?.(d.transcript, d);
            if (d.auto_submit) onAutoSubmit?.(d.transcript, d);
          } catch (e) {
            setError(e?.response?.data?.detail || String(e));
          }
          setIsProcessing(false);
        };
        mediaRef.current = rec;
        rec.start();
        setIsListening(true);
      } catch (e) {
        setError("Microphone access denied or unavailable");
        // Fallback to Web Speech API
        return startFallback();
      }
    } else {
      return startFallback();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, prompt, onTranscript, onAutoSubmit, useWhisper]);

  const startFallback = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setError("Speech recognition not supported in this browser"); return; }
    const rec = new SR();
    rec.lang = language === "hi" ? "hi-IN" : "en-IN";
    rec.continuous = false;
    rec.interimResults = false;
    rec.onresult = async (e) => {
      const text = Array.from(e.results)
          .map(r => r[0].transcript).join(" ");
      setRaw(text);
      setIsProcessing(true);
      try {
        const r = await api.post("/voice/process",
            { transcript: text, language });
        const d = r.data;
        setTranscript(d.transcript);
        setHadCorrections(d.had_corrections);
        setIntent(d.intent);
        setAutoSubmit(!!d.auto_submit);
        onTranscript?.(d.transcript, d);
        if (d.auto_submit) onAutoSubmit?.(d.transcript, d);
      } catch (err) {
        setError(String(err));
      }
      setIsProcessing(false);
    };
    rec.onend = () => setIsListening(false);
    rec.onerror = (e) => { setError(e.error || "Recognition error"); setIsListening(false); };
    rec.start();
    fallbackRecRef.current = rec;
    setIsListening(true);
  }, [language, onTranscript, onAutoSubmit]);

  const stop = useCallback(() => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      try { mediaRef.current.stop(); } catch (_) { /* ok */ }
    }
    if (fallbackRecRef.current) {
      try { fallbackRecRef.current.stop(); } catch (_) { /* ok */ }
      fallbackRecRef.current = null;
    }
    setIsListening(false);
  }, []);

  const toggle = useCallback(() => {
    isListening ? stop() : start();
  }, [isListening, start, stop]);

  // Cleanup on unmount
  useEffect(() => () => { stop(); stopStream(); }, [stop]);

  return { isListening, isProcessing, transcript, raw, hadCorrections,
              intent, autoSubmit, error, start, stop, toggle, reset };
}


/** Speak text out loud using browser SpeechSynthesis. */
export function speak(text, { lang = "en-IN", rate = 1.0 } = {}) {
  if (!text || !window.speechSynthesis) return;
  try {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = lang; u.rate = rate;
    window.speechSynthesis.speak(u);
  } catch (_) { /* ok */ }
}


/** Spacebar push-to-talk hook — hold space to start, release to stop. */
export function useSpacebarPushToTalk({ enabled = true, onStart, onStop }) {
  const downRef = useRef(false);
  useEffect(() => {
    if (!enabled) return;
    const isInputEl = (el) => el && (el.tagName === "INPUT"
        || el.tagName === "TEXTAREA"
        || el.isContentEditable);
    const onDown = (e) => {
      // Skip if focus is on a regular input (don't hijack typing)
      if (isInputEl(document.activeElement)) return;
      if (e.code === "Space" && !downRef.current && !e.repeat) {
        e.preventDefault();
        downRef.current = true;
        onStart?.();
      }
    };
    const onUp = (e) => {
      if (e.code === "Space" && downRef.current) {
        e.preventDefault();
        downRef.current = false;
        onStop?.();
      }
    };
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup", onUp);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup", onUp);
    };
  }, [enabled, onStart, onStop]);
}


/** Wake-word listener — uses browser SpeechRecognition in continuous mode
 *  to listen for "Hey Capexara Process Industry" / "OK Capexara Process Industry", then triggers onWake. */
export function useWakeWord({ enabled = false, wakeWord = "Hey Capexara Process Industry", onWake }) {
  const recRef = useRef(null);
  useEffect(() => {
    if (!enabled) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const rec = new SR();
    rec.lang = "en-IN";
    rec.continuous = true;
    rec.interimResults = true;
    const wakeRe = new RegExp(`\\b${wakeWord.replace(/\s+/g, "\\s+")}\\b`, "i");
    const altRe = /\b(ok|okay|hey)\s+rachana\b/i;
    rec.onresult = (e) => {
      const last = e.results[e.results.length - 1];
      if (!last) return;
      const text = last[0].transcript.trim();
      if (wakeRe.test(text) || altRe.test(text)) {
        try { rec.stop(); } catch (_) { /* ok */ }
        onWake?.();
      }
    };
    rec.onend = () => { /* auto-restart while enabled */
      if (recRef.current === rec) {
        try { rec.start(); } catch (_) { /* ok */ }
      }
    };
    rec.onerror = () => { /* swallow */ };
    try { rec.start(); recRef.current = rec; } catch (_) { /* ok */ }
    return () => {
      recRef.current = null;
      try { rec.stop(); } catch (_) { /* ok */ }
    };
  }, [enabled, wakeWord, onWake]);
}
